import React, { useState } from "react";
import {
  Box, Typography, Button, Card, CardContent, Chip, CircularProgress, Grid, Avatar, Stack, Divider,
} from "@mui/material";
import { FiLink2, FiArrowUpRight, FiRadio, FiX, FiCheckCircle } from "react-icons/fi";
import { EmptyState, ConfirmDialog, useTokens } from "./shared";

export function FacebookActions({ fb }) {
  const tokens = useTokens();
  const { fbConnected, handleConnectFacebook } = fb;
  return (
    <Button
      variant={fbConnected ? "outlined" : "contained"}
      onClick={handleConnectFacebook}
      startIcon={fbConnected ? <FiArrowUpRight size={16} /> : <FiLink2 size={16} />}
      sx={{
        bgcolor: fbConnected ? "transparent" : tokens.fb,
        color: fbConnected ? tokens.ink : "#fff",
        borderColor: fbConnected ? tokens.border : tokens.fb,
        "&:hover": { bgcolor: fbConnected ? tokens.hoverTint : tokens.fbDark },
        textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none",
      }}
    >
      {fbConnected ? "Add / reconnect pages" : "Connect Facebook"}
    </Button>
  );
}

export function FacebookPagesTab({ fb }) {
  const tokens = useTokens();
  const { pages, fbLoading, fbConnected, disconnectingPageId, handleDisconnectPage, handleConnectFacebook } = fb;
  const [target, setTarget] = useState(null);

  if (fbLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} thickness={4} sx={{ color: tokens.fb }} />
      </Box>
    );
  }
  if (!fbConnected) {
    return (
      <Stack alignItems="center">
        <EmptyState
          icon={<FiRadio size={20} color={tokens.inkFaint} />}
          title="No pages connected yet"
          caption="Connect a Facebook page to start syncing leads into your CRM automatically."
        />
        <Button
          variant="contained"
          onClick={handleConnectFacebook}
          startIcon={<FiLink2 size={16} />}
          sx={{ bgcolor: tokens.fb, "&:hover": { bgcolor: tokens.fbDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
        >
          Connect Facebook
        </Button>
      </Stack>
    );
  }
  return (
    <>
      <Grid container spacing={2}>
        {pages.map((page) => (
          <Grid item xs={12} sm={6} lg={4} key={page.PageId}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderColor: tokens.border, bgcolor: tokens.surface }}>
              <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                  <Avatar sx={{ bgcolor: tokens.fb, width: 38, height: 38, fontWeight: 700 }}>
                    {page.PageName?.[0]?.toUpperCase() || "F"}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: tokens.ink }}>
                      {page.PageName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.inkFaint }}>ID: {page.PageId}</Typography>
                  </Box>
                  <Chip label="Active" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: tokens.successBg, color: tokens.success }} />
                </Stack>
                <Typography variant="caption" display="block" sx={{ color: tokens.inkFaint, mb: 1.5 }}>
                  Connected: {page.ConnectedAt ? new Date(page.ConnectedAt).toLocaleString() : "—"}
                </Typography>
                <Button
                  size="small"
                  fullWidth
                  onClick={() => setTarget(page)}
                  disabled={disconnectingPageId === page.PageId}
                  startIcon={<FiX size={14} />}
                  sx={{ textTransform: "none", fontWeight: 600, color: tokens.danger, "&:hover": { bgcolor: tokens.dangerBg } }}
                >
                  Disconnect page
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <ConfirmDialog
        open={!!target}
        title="Disconnect this page?"
        message={`Leads from "${target?.PageName || "this page"}" will stop syncing into the CRM.`}
        confirmLabel="Disconnect"
        loading={!!target && disconnectingPageId === target.PageId}
        onClose={() => setTarget(null)}
        onConfirm={async () => {
          await handleDisconnectPage(target.PageId);
          setTarget(null);
        }}
      />
    </>
  );
}

export function FacebookGuideTab() {
  const tokens = useTokens();
  const steps = [
    "Click “Connect Facebook” and sign in with the account that manages your pages.",
    "Grant access to the pages whose Lead Ads you want to sync.",
    "New leads from those pages appear in the CRM automatically.",
    "Disconnect a page at any time to stop syncing from it.",
  ];
  return (
    <Stack spacing={0} divider={<Divider sx={{ borderColor: tokens.border }} />}>
      {steps.map((s, i) => (
        <Stack key={i} direction="row" spacing={2} alignItems="center" py={2}>
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: tokens.fb, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {i + 1}
          </Box>
          <Typography variant="body2" sx={{ color: tokens.ink }}>{s}</Typography>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} alignItems="center" pt={2}>
        <FiCheckCircle color={tokens.success} />
        <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
          Permissions requested: pages list, page engagement, lead retrieval and page metadata.
        </Typography>
      </Stack>
    </Stack>
  );
}
